/**
 * Substituicao de placeholders {{x.y.z}} em textos personalizados.
 *
 * Mesmo padrao usado em Macros via handleVariablesToReplacePlaceholders, mas
 * isolado em utilitario para poder ser reaproveitado em Default Actions e
 * Quick Responses sem duplicar logica.
 *
 * Como funciona:
 *   1. replacePlaceholders(text, vars) — extrai placeholders presentes no texto
 *      via regex e substitui apenas os que tiverem valor em `vars`. Placeholders
 *      sem valor sao substituidos por string vazia (mesma estrategia das macros).
 *   2. buildChatVars(chat, userSession) — monta o dicionario de variaveis a partir
 *      do chat ativo (ja carregado no state global do 2talk) + atendente logado.
 *
 * Por que frontend e nao backend: macros ja resolvem assim. Os dados ja estao na
 * tela quando o usuario aplica a quick response, entao zero latencia de rede,
 * zero endpoint novo. Para campos nao expandidos no chat.* (channel.name,
 * team.name, etc.), e possivel estender este helper futuramente recebendo
 * listas auxiliares (channelsList, teamsList) ja carregadas pelo callsite.
 */

const PLACEHOLDER_REGEX = /\{\{([^}]+)\}\}/g;

/**
 * Substitui placeholders {{x.y}} no texto pelos valores correspondentes em vars.
 * Placeholders nao encontrados viram string vazia (igual `... || ''` que macros fazem).
 *
 * @param {string} text
 * @param {Object<string, string>} vars
 * @returns {string}
 */
export function replacePlaceholders(text, vars) {
  if (!text || typeof text !== "string") return text || "";
  if (!vars || typeof vars !== "object") return text;

  return text.replace(PLACEHOLDER_REGEX, (_match, key) => {
    const trimmedKey = key.trim();
    if (Object.prototype.hasOwnProperty.call(vars, trimmedKey)) {
      const val = vars[trimmedKey];
      return val === null || val === undefined ? "" : String(val);
    }
    return ""; // placeholder desconhecido -> remove (mesma estrategia das macros)
  });
}

/**
 * Monta o dicionario de variaveis disponiveis em uma conversa de chat.
 * Le do `chats.chat` (Redux state global do 2talk) + userSession (atendente logado).
 *
 * Importante:
 *   - Usa apenas dados ja disponiveis em memoria (chat ativo + sessao)
 *   - Campos nao expandidos sao retornados como string vazia, igual macros fazem
 *   - As listas auxiliares (channelsList, providersList, etc.) podem ser passadas
 *     no parametro `lookups` para resolver nomes que dependem so de id no chat.
 *
 * @param {Object} chat - objeto chats.chat (Redux)
 * @param {Object} userSession - objeto userSession.user
 * @param {Object} [lookups] - listas auxiliares opcionais para resolver nomes via id
 * @returns {Object<string, string>}
 */
export function buildChatVars(chat, userSession, lookups = {}) {
  const c = chat || {};
  const u = (userSession && userSession.user) || {};
  const customer = c.customer || {};

  const safe = (v) => (v === null || v === undefined ? "" : String(v));

  // Resolver name via lookups: lookups[listKey].find(x => x.id === id)?.name
  const findName = (listKey, id) => {
    if (id === null || id === undefined) return "";
    const list = lookups[listKey];
    if (!Array.isArray(list) || list.length === 0) return "";
    const found = list.find((x) => x && (x.id === id || x.id === Number(id)));
    return found && found.name ? String(found.name) : "";
  };

  // Formatar created_at se vier ISO
  let createdFormatted = "";
  if (c.createdat) {
    try {
      const d = new Date(c.createdat);
      if (!isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        createdFormatted = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } else {
        createdFormatted = String(c.createdat);
      }
    } catch {
      createdFormatted = String(c.createdat);
    }
  }

  return {
    // diretos no chat
    "chat.id": safe(c.id),
    "chat.customer.name": safe(customer.name),
    "chat.customer.phone": safe(customer.phone),
    "chat.customer.email": safe(customer.email),
    "chat.assigned.name": safe(c.assignedusername),
    "chat.organization.name": safe(c.organization && c.organization.name),
    "chat.priority.name": safe(c.priority && c.priority.name),
    "chat.category.name": safe(c.organizationcategory && c.organizationcategory.name),
    "chat.created": createdFormatted,

    // via lookups (resolve so se a lista foi passada; senao fica vazio)
    "chat.channel.name": findName("channelsList", c.channelid),
    "chat.team.name": findName("teamsList", c.techinicalteamid),
    "chat.provider.name": findName("providersList", c.messageproviderid),
    "chat.number.phone": findName("numbersList", c.registrationnumberrequestid),
    "chat.status.name": findName("statusList", c.chatstatusid),
    "chat.origin.name": findName("originRequestList", c.originrequestid),
    "chat.webchat.name": findName("webchatList", c.webchatid),
    "chat.chatbot.name": findName("chatbotList", c.chatbotid),

    // usuario logado (atendente)
    "user.name": safe(u.name),
    "user.email": safe(u.email),
  };
}
